import { t } from "i18next";
import { type Dayjs } from "dayjs";
import { ClipSwitch } from "@components/ClipSwitch";
import { ClipDatePicker } from "@components/ClipDatePicker";
import { twMerge } from "tailwind-merge";
import { getTimeBoundsInTimezone, getNowInTimezone } from "@utils/timezone";
import { DEFAULT_TIMEZONE } from "@src/config/timezone";
import { TestId } from "@shared/testIds";

export interface ScheduledDateValue {
  scheduled?: boolean;
  time?: Dayjs | null;
}

export interface ScheduledDateProps {
  className?: string;
  disabled?: boolean;
  value?: ScheduledDateValue;
  error?: boolean;
  errorText?: string;
  targetTimezone?: string;
  onChange?: (value: ScheduledDateValue) => void;
}

const getDateBounds = (tz: string) => {
  return getTimeBoundsInTimezone(tz, 7);
};

export const ScheduledDate = (props: ScheduledDateProps) => {
  const { className, disabled, value, error, errorText, targetTimezone, onChange } = props;
  const scheduled = !!value?.scheduled;
  const selectedTime = value?.time ?? null;

  const targetTz = targetTimezone || DEFAULT_TIMEZONE;
  const { minDate, maxDate } = getDateBounds(targetTz);

  return (
    <div className={twMerge("flex flex-col gap-[4px]", className)}>
      <ClipSwitch
        data-testid={TestId.SchedulePostSwitch}
        checked={scheduled}
        disabled={disabled}
        title={t("Schedule Post")}
        onChange={(checked) => {
          if (!checked) {
            onChange?.({ scheduled: false, time: undefined });
            return;
          }
          onChange?.({ scheduled: true, time: selectedTime ?? undefined });
        }}
      />

      {scheduled ? (
        <div className="flex flex-col gap-[4px]">
          <ClipDatePicker
            data-testid={TestId.SchedulePostDatePicker}
            allowClear
            className="h-[32px] w-[320px] rounded-[8px]"
            placeholder={t("Next 7 days only")}
            disabled={disabled}
            panelRender={(panel) => <div data-testid={TestId.SchedulePostDatePickerDropdown}>{panel}</div>}
            value={selectedTime?.tz(targetTz) ?? null}
            format="YYYY-MM-DD HH:mm"
            showTime={{
              format: "HH:mm",
              minuteStep: 30,
            }}
            disabledDate={(current) => {
              if (!current) return false;
              const dayStartInTz = current.tz(targetTz, true).startOf("day");
              const dayEndInTz = current.tz(targetTz, true).endOf("day");
              return dayEndInTz.isBefore(minDate) || dayStartInTz.isAfter(maxDate);
            }}
            disabledTime={(current) => {
              if (!current) return {};
              const nowInTz = getNowInTimezone(targetTz);
              const currentInTz = current.tz(targetTz, true);

              if (!currentInTz.isSame(nowInTz, "day")) return {};

              const disabledHours = Array.from({ length: nowInTz.hour() }, (_, index) => index);
              const disabledMinutes = (selectedHour: number) => {
                if (selectedHour !== nowInTz.hour()) return [];
                return Array.from({ length: nowInTz.minute() }, (_, index) => index);
              };
              return {
                disabledHours: () => disabledHours,
                disabledMinutes,
              };
            }}
            onChange={(nextValue) => {
              if (!nextValue) {
                onChange?.({
                  scheduled: true,
                  time: undefined,
                });
                return;
              }
              // 将用户选择的日期/时间解释为目标时区的时间
              const timeInTargetTz = nextValue.tz(targetTz, true);
              onChange?.({
                scheduled: true,
                time: timeInTargetTz,
              });
            }}
          />
          {error ? (
            <div data-testid={TestId.SchedulePostError} className="text-kds-danger text-[12px] leading-[16px]">
              {t(errorText || "Please select date and time")}
            </div>
          ) : (
            // 文案，暂时固定为美东时区，因为涉及翻译问题，后续再支持多时区文案
            // <div
            //   className="text-[12px] leading-[16px] text-kds-fg-subtle"
            // >
            //   {t("Timezone: {{timezone}}", { timezone: getTimezoneDisplayName(targetTz) })}
            // </div>
            <div
              data-testid={TestId.SchedulePostTimezoneLabel}
              className="text-[12px] leading-[16px] text-kds-fg-subtle"
            >
              {t("Timezone: Eastern Time")}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};
